import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  appendClinicalElementScheme,
  appendPlanScheme,
  createPlanOption,
  deleteClinicalElementScheme,
  deleteMovementStepMesh,
  deletePlanOption,
  deletePlanOptionMesh,
  deletePlanScheme,
  deletePlanSchemeAsset,
  fetchPlanSchemeBlob,
  publishPlanOption,
  reprocessMesh,
  setPlanSelection,
  updatePlanOption,
  uploadClinicalElementScheme,
  uploadMovementStepMesh,
  uploadPlanOptionMesh,
  uploadPlanScheme,
  type CycleRef,
  type PlanMeshRole,
  type PlanOptionPayload,
  type PlanSchemeSlot,
} from '../../../api/plan-options';
import type { JawType } from '../../../types/admin';
import { useAuth } from '../../auth/AuthProvider';

function useRequireToken() {
  const { tokens } = useAuth();
  return () => {
    if (!tokens?.access_token) {
      throw new Error('Authenticated admin session is required.');
    }
    return tokens.access_token;
  };
}

async function invalidateTreatmentQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  patientId: string,
) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['treatments', patientId] }),
    queryClient.invalidateQueries({ queryKey: ['patient', patientId] }),
  ]);
}

export function useCreatePlanOption(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: PlanOptionPayload) => createPlanOption(cycle, payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type UpdatePlanOptionVariables = {
  readonly optionId: string;
  readonly payload: PlanOptionPayload;
};

export function useUpdatePlanOption(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, payload }: UpdatePlanOptionVariables) =>
      updatePlanOption(cycle, optionId, payload, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export function useDeletePlanOption(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (optionId: string) => deletePlanOption(cycle, optionId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type PublishPlanOptionVariables = {
  readonly optionId: string;
  readonly isPublished: boolean;
};

export function usePublishPlanOption(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, isPublished }: PublishPlanOptionVariables) =>
      publishPlanOption(cycle, optionId, isPublished, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

/** `null` clears the selection made on the patient's behalf. */
export function useSetPlanSelection(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (planOptionId: string | null) => setPlanSelection(cycle, planOptionId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type UploadPlanMeshVariables = {
  readonly optionId: string;
  readonly role: PlanMeshRole;
  readonly jaw: JawType;
  readonly file: File;
};

export function useUploadPlanOptionMesh(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, role, jaw, file }: UploadPlanMeshVariables) =>
      uploadPlanOptionMesh(cycle, optionId, role, jaw, file, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type DeletePlanMeshVariables = {
  readonly optionId: string;
  readonly role: PlanMeshRole;
  readonly jaw: JawType;
};

export function useDeletePlanOptionMesh(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, role, jaw }: DeletePlanMeshVariables) =>
      deletePlanOptionMesh(cycle, optionId, role, jaw, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type UploadPlanSchemeVariables = {
  readonly optionId: string;
  readonly slot: PlanSchemeSlot;
  readonly file: File;
};

export function useUploadPlanScheme(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, slot, file }: UploadPlanSchemeVariables) =>
      uploadPlanScheme(cycle, optionId, slot, file, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export function useAppendPlanScheme(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, slot, file }: UploadPlanSchemeVariables) =>
      appendPlanScheme(cycle, optionId, slot, file, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type DeletePlanSchemeVariables = {
  readonly optionId: string;
  readonly slot: PlanSchemeSlot;
};

export function useDeletePlanScheme(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, slot }: DeletePlanSchemeVariables) =>
      deletePlanScheme(cycle, optionId, slot, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type UploadClinicalElementSchemeVariables = {
  readonly optionId: string;
  readonly elementId: string;
  readonly file: File;
};

export function useUploadClinicalElementScheme(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, elementId, file }: UploadClinicalElementSchemeVariables) =>
      uploadClinicalElementScheme(cycle, optionId, elementId, file, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export function useAppendClinicalElementScheme(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, elementId, file }: UploadClinicalElementSchemeVariables) =>
      appendClinicalElementScheme(cycle, optionId, elementId, file, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type DeletePlanSchemeAssetVariables = {
  readonly optionId: string;
  readonly schemeId: string;
};

export function useDeletePlanSchemeAsset(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, schemeId }: DeletePlanSchemeAssetVariables) =>
      deletePlanSchemeAsset(cycle, optionId, schemeId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type DeleteClinicalElementSchemeVariables = {
  readonly optionId: string;
  readonly elementId: string;
};

export function useDeleteClinicalElementScheme(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ optionId, elementId }: DeleteClinicalElementSchemeVariables) =>
      deleteClinicalElementScheme(cycle, optionId, elementId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

/** Creates a short-lived object URL from authenticated scheme bytes. */
export function usePlanSchemePreviewUrl(
  cycle: CycleRef,
  optionId: string,
  schemeId: string | null,
  enabled = true,
) {
  const { tokens } = useAuth();
  const token = tokens?.access_token ?? null;
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !schemeId || !token) {
      setUrl(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let objectUrl: string | null = null;
    let active = true;
    setUrl(null);
    setIsLoading(true);
    setError(null);

    void fetchPlanSchemeBlob(cycle, optionId, schemeId, token, {
      signal: controller.signal,
    }).then(
      (blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setIsLoading(false);
      },
      (reason: unknown) => {
        if (!active || controller.signal.aborted) return;
        setError(reason instanceof Error ? reason : new Error('Failed to load scheme image.'));
        setIsLoading(false);
      },
    );

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cycle.patientId, cycle.treatmentId, cycle.cycleId, optionId, schemeId, token, enabled]);

  const shouldLoad = Boolean(enabled && schemeId && token);
  return {
    url,
    isLoading: shouldLoad && (isLoading || (!url && !error)),
    error,
  } as const;
}

export type UploadStepMeshVariables = {
  readonly stepIndex: number;
  readonly jaw: JawType;
  readonly file: File;
};

export function useUploadMovementStepMesh(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stepIndex, jaw, file }: UploadStepMeshVariables) =>
      uploadMovementStepMesh(cycle, stepIndex, jaw, file, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

export type DeleteStepMeshVariables = {
  readonly stepIndex: number;
  readonly jaw: JawType;
};

export function useDeleteMovementStepMesh(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ stepIndex, jaw }: DeleteStepMeshVariables) =>
      deleteMovementStepMesh(cycle, stepIndex, jaw, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}

/** Retry GLB generation for a mesh whose server-side conversion failed. */
export function useReprocessMesh(cycle: CycleRef) {
  const getToken = useRequireToken();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (meshId: string) => reprocessMesh(cycle, meshId, getToken()),
    onSuccess: () => invalidateTreatmentQueries(queryClient, cycle.patientId),
  });
}
